import * as React from 'react';

import {useMutation} from '@apollo/react-hooks';

import type {
    CreateAudience as CreateAudienceMutation,
    CreateAudienceVariables as CreateAudienceMutationVariables,
    DeleteAudience as DeleteAudienceMutation,
    DeleteAudienceVariables as DeleteAudienceMutationVariables,
    RestoreAudience as RestoreAudienceMutation,
    RestoreAudienceVariables as RestoreAudienceMutationVariables,
    GetAudiences as GetAudiencesQuery,
    GetAudience as GetAudienceQuery,
    UpdateAudience as UpdateAudienceMutation,
    UpdateAudienceVariables as UpdateAudienceMutationVariables,
    AudienceUpdateInput,
    GetAudiencesVariables as GetAudiencesQueryVariables,
} from 'nutshell-graphql-types';

import CREATE_AUDIENCE from './graphql/mutations/create-audience.graphql';
import DELETE_AUDIENCE from './graphql/mutations/delete-audience.graphql';
import RESTORE_AUDIENCE from './graphql/mutations/restore-audience.graphql';
import UPDATE_AUDIENCE from './graphql/mutations/update-audience.graphql';
import GET_AUDIENCE from './graphql/queries/get-audience.graphql';
import GET_AUDIENCES from './graphql/queries/get-audiences.graphql';

type Props = {
    children: (arg1: {
        onCreate: (name: string, queryVariables?: GetAudiencesQueryVariables) => Promise<any>;
        onDelete: (audienceId: string, queryVariables?: GetAudiencesQueryVariables) => Promise<any>;
        onRestore: (
            audienceId: string,
            queryVariables?: GetAudiencesQueryVariables
        ) => Promise<any>;
        onUpdate: (input: AudienceUpdateInput) => Promise<any>;
    }) => React.ReactElement;
};

export function AudienceMutations(props: Props) {
    const [createAudience] = useMutation<CreateAudienceMutation, CreateAudienceMutationVariables>(
        CREATE_AUDIENCE
    );

    const [deleteAudience] = useMutation<DeleteAudienceMutation, DeleteAudienceMutationVariables>(
        DELETE_AUDIENCE
    );

    const [restoreAudience] = useMutation<
        RestoreAudienceMutation,
        RestoreAudienceMutationVariables
    >(RESTORE_AUDIENCE);

    const [updateAudience] = useMutation<UpdateAudienceMutation, UpdateAudienceMutationVariables>(
        UPDATE_AUDIENCE
    );

    return props.children({
        onCreate: (name: string, queryVariables?: GetAudiencesQueryVariables) =>
            createAudience({
                variables: {audience: {audience: {name: name}}},
                update: (proxy, mutationResults) => {
                    const newAudience =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.audienceCreate &&
                        mutationResults.data.audienceCreate.audience;

                    if (!newAudience) return;

                    // This will "filter" the new audience from the list of
                    // archived audeinces, but it's not in that list so its fine
                    audienceDeleteRestoreUpdate(proxy, newAudience, false, queryVariables);
                },
            }).then(
                // Get the audience for any aditional promise chaining
                (mutationResults) =>
                    mutationResults &&
                    mutationResults.data &&
                    mutationResults.data.audienceCreate &&
                    mutationResults.data.audienceCreate.audience
            ),
        onRestore: (audienceId: string, queryVariables?: GetAudiencesQueryVariables) =>
            restoreAudience({
                variables: {id: audienceId},
                update: (proxy, mutationResults) => {
                    // Get the restored audience
                    const restoredAudience =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.audienceRestore &&
                        mutationResults.data.audienceRestore.audience;

                    if (!restoredAudience) return;

                    audienceDeleteRestoreUpdate(proxy, restoredAudience, false, queryVariables);
                },
            }),
        onDelete: (audienceId: string, queryVariables?: GetAudiencesQueryVariables) =>
            deleteAudience({
                variables: {id: audienceId},
                update: (proxy, mutationResults) => {
                    // Get the archived audience
                    const archivedAudience =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.audienceDelete &&
                        mutationResults.data.audienceDelete.audience;

                    if (!archivedAudience) return;

                    audienceDeleteRestoreUpdate(proxy, archivedAudience, true, queryVariables);
                },
            }),
        onUpdate: (saveData: AudienceUpdateInput) => {
            return updateAudience({
                variables: {
                    audience: saveData,
                },
                update: (proxy, mutationResults) => {
                    // Read the data from our cache for this query.
                    const cacheResult: GetAudienceQuery | null | undefined = proxy.readQuery({
                        query: GET_AUDIENCE,
                        variables: {audienceId: saveData.audienceId},
                    });

                    if (!cacheResult || !cacheResult.audience) {
                        return;
                    }

                    const updatedAudience =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.audienceUpdate &&
                        mutationResults.data.audienceUpdate.audience;
                    if (!updatedAudience) return;

                    proxy.writeQuery({
                        query: GET_AUDIENCE,
                        variables: {audienceId: saveData.audienceId},
                        data: {
                            ...cacheResult.audience,
                            audience: updatedAudience,
                        },
                    });
                },
            });
        },
    });
}

/**
 * This will handle updating our local cache when we archive or restore an
 * audience. It correctly handles updating both the archived and unarchived
 * caches.
 *
 * @param {DataProxy} proxy The mutation update proxy
 * @param {Audience} audience The audience that was mutated
 * @param {boolean} isDeleting True if we are deleting the given audience,
 * false if we are restoring it.
 */
const audienceDeleteRestoreUpdate = (
    // @ts-expect-error
    proxy,
    // @ts-expect-error
    audience,
    isDeleting: boolean,
    queryVariables?: GetAudiencesQueryVariables
) => {
    // Passing a value for `isArchived` here isn't really needed because we
    // overwrite it when writing to the cache below, but hey I made it required
    const variables = queryVariables || {};

    try {
        // Update our archived cache
        // This isn't the best variable name, but its the cache we're removing
        // an audience from.
        const cacheResultRemoving: GetAudiencesQuery | null | undefined = proxy.readQuery({
            query: GET_AUDIENCES,
            variables: {...variables, archived: !isDeleting},
        });

        if (cacheResultRemoving && cacheResultRemoving.audiences) {
            proxy.writeQuery({
                query: GET_AUDIENCES,
                variables: {...variables, archived: !isDeleting},
                data: {
                    audiences: {
                        ...cacheResultRemoving.audiences,
                        edges: cacheResultRemoving.audiences.edges.filter((edge) => {
                            return edge.node.id !== audience.id;
                        }),
                    },
                },
            });
        }
    } catch {
        // If the above query doesn't exist in the cache, Apollo will throw an
        // error (why it can't just return null is beyond me). If that happens,
        // we need to "catch" the error. But we don't want to really do anything
        // with it. The just means we haven't performed this query yet. So, an
        // empty catch block
    }

    // Update our unarchived cache
    // Again, not the best name. We're adding an audience to this cache
    try {
        const cacheResultAdding: GetAudiencesQuery | null | undefined = proxy.readQuery({
            query: GET_AUDIENCES,
            variables: {...variables, archived: isDeleting},
        });

        if (!cacheResultAdding || !cacheResultAdding.audiences) {
            return;
        }

        proxy.writeQuery({
            query: GET_AUDIENCES,
            variables: {...variables, archived: isDeleting},
            data: {
                audiences: {
                    ...cacheResultAdding.audiences,
                    edges: [
                        {
                            cursor: audience.id,
                            node: audience,
                            __typename: 'AudiencesEdge',
                        },
                    ].concat(cacheResultAdding.audiences.edges),
                },
            },
        });
    } catch {
        // If the above query doesn't exist in the cache, Apollo will throw an
        // error (why it can't just return null is beyond me). If that happens,
        // we need to "catch" the error. But we don't want to really do anything
        // with it. The just means we haven't performed this query yet. So, an
        // empty catch block
    }
};
